import React from 'react';
import { cn } from '@/lib/utils';
import type { ClientHealthMetrics } from '@/types/client';
import { getWarmthLevel } from '@/types/client';
import { WARMTH_LABELS, WARMTH_COLORS } from '../shared/stageConstants';

interface ComputedMetricsRowProps {
  healthMetrics: ClientHealthMetrics | null;
}

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  colorClass?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, subValue, colorClass }) => (
  <div className="bg-surface-2 border border-border rounded p-2.5 sm:p-3 flex flex-col items-center text-center min-w-0">
    <span className="text-[10px] font-label uppercase tracking-wider text-muted-foreground mb-1 truncate w-full">
      {label}
    </span>
    <span className={cn('font-mono text-base sm:text-lg font-medium truncate w-full', colorClass || 'text-foreground')}>
      {value}
    </span>
    {subValue && (
      <span className="text-[10px] font-label text-muted-foreground mt-0.5 truncate w-full">{subValue}</span>
    )}
  </div>
);

export const ComputedMetricsRow: React.FC<ComputedMetricsRowProps> = ({ healthMetrics }) => {
  const m = healthMetrics;
  const warmth = getWarmthLevel(m?.warmth_ratio ?? null);
  const warmthColors = WARMTH_COLORS[warmth];

  const cancellationColor =
    m?.cancellation_rate != null && m.cancellation_rate > 20
      ? 'text-led-red'
      : undefined;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
      <MetricCard
        label="Revenue"
        value={m?.total_revenue != null ? `$${m.total_revenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '--'}
        colorClass="text-primary"
      />
      <MetricCard
        label="Total Gigs"
        value={m?.gig_count != null ? `${m.gig_count}` : '--'}
      />
      <MetricCard
        label="Avg Days to Pay"
        value={m?.avg_days_to_payment != null ? `${Math.round(m.avg_days_to_payment)}d` : '--'}
      />
      <MetricCard
        label="Cancel Rate"
        value={m?.cancellation_rate != null ? `${m.cancellation_rate.toFixed(0)}%` : '--'}
        colorClass={cancellationColor}
      />
      <MetricCard
        label="Warmth"
        value={WARMTH_LABELS[warmth]}
        subValue={m?.warmth_ratio != null ? `${m.warmth_ratio.toFixed(1)} ratio` : undefined}
        colorClass={warmthColors.text}
      />
    </div>
  );
};
